/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ConferencesPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Conference Rooms listing page - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Trash2, Copy, Code, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useDebugMode } from '../../hooks/useDebugMode';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import { deleteConferenceById, navigateToConferenceDuplicate } from './conferencesActions';
import { useConferencesList } from './hooks/useConferencesList';
import { fetchConferenceXml } from './api/conferencesApi';

export default function ConferencesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isActive: isDebugModeActive } = useDebugMode();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; conference: { id: number; uuid?: string; extension: string; name?: string } | null }>({
    isOpen: false,
    conference: null,
  });
  const [xmlModal, setXmlModal] = useState<{ isOpen: boolean; conference: { id: number; uuid?: string; extension: string } | null; xml: string }>({
    isOpen: false,
    conference: null,
    xml: '',
  });

  // Define columns
  const availableColumns = useMemo(() => [
    {
      key: 'extension',
      label: t('conferences:columns.extension'),
      sortable: true,
      visible: true,
      render: (_value: unknown, item: { extension?: string }) => (
        <span className="font-medium text-primary">{item?.extension || '-'}</span>
      ),
    },
    {
      key: 'name',
      label: t('conferences:columns.name'),
      sortable: true,
      visible: true,
      render: (_value: unknown, item: { name?: string }) => item?.name || '-',
    },
    {
      key: 'description',
      label: t('conferences:columns.description'),
      sortable: true,
      visible: true,
      render: (_value: unknown, item: { description?: string }) => item?.description || '-',
    },
    {
      key: 'enabled',
      label: t('conferences:columns.enabled'),
      sortable: true,
      visible: true,
      render: (_value: unknown, item: { enabled?: boolean }) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
          {item?.enabled ? t('common:enabled') : t('common:disabled')}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: t('conferences:columns.createdAt'),
      sortable: true,
      visible: false,
      render: (_value: unknown, item: { created_at: string }) => new Date(item.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: t('common:actions'),
      sortable: false,
      visible: true,
    },
  ], [t]);

  // Use custom hook for list management
  const {
    data,
    loading,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    columns,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useConferencesList({
    initialColumns: availableColumns,
    initialSortBy: 'extension',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  // Merge columns with custom renderers
  const enhancedColumns = useMemo(() => {
    const baseColumns = columns.length > 0 ? columns : availableColumns;
    return baseColumns.map((col) => {
      if (col.key === 'actions') {
        return {
          ...col,
          render: (_value: unknown, item: { id: number; uuid?: string; extension: string; name?: string }) => renderActions(item),
        };
      }
      return col;
    });
  }, [columns, availableColumns]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/conferences/new');
  }, [navigate]);

  const handleEdit = useCallback((conference: { id: number; uuid?: string }) => {
    navigate(`/pbx/applications/conferences/${conference.uuid || conference.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((conference: { id: number; uuid?: string }) => {
    navigateToConferenceDuplicate(navigate, conference.uuid || conference.id);
  }, [navigate]);

  const handleDelete = useCallback((conference: { id: number; uuid?: string; extension: string; name?: string }) => {
    setDeleteModal({ isOpen: true, conference });
  }, []);

  const handleViewXml = useCallback(async (conference: { id: number; uuid?: string; extension: string }) => {
    try {
      const xmlData = await fetchConferenceXml(conference.uuid || conference.id);
      setXmlModal({
        isOpen: true,
        conference: conference,
        xml: xmlData
      });
    } catch (error) {
      console.error('Error fetching XML:', error);
      const errorMessage = error instanceof Error ? error.message : t('conferences:messages.xmlError');
      showError(errorMessage);
    }
  }, [showError, t]);

  // Render actions for each row
  const renderActions = useCallback((conference: { id: number; uuid?: string; extension: string; name?: string }) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common:edit')} position="top">
        <button
          onClick={() => handleEdit(conference)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common:duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(conference)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>

      {isDebugModeActive && (
        <SimpleTooltip content={t('conferences:actions.viewXml')} position="top">
          <button
            onClick={() => handleViewXml(conference)}
            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Code className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </button>
        </SimpleTooltip>
      )}

      <SimpleTooltip content={t('common:delete')} position="top">
        <button
          onClick={() => handleDelete(conference)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [t, handleEdit, handleDuplicate, handleDelete, isDebugModeActive, handleViewXml]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.conference) return;

    try {
      await deleteConferenceById(deleteModal.conference.uuid || deleteModal.conference.id);
      showSuccess(t('conferences:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, conference: null });
      await refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('conferences:messages.deleteError'));
    }
  }, [deleteModal, refresh, showSuccess, showError, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            titleIcon={<Users className="h-5 w-5 text-primary" />}
            loading={loading}
            columns={enhancedColumns}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            searchPlaceholder={t('conferences:searchPlaceholder')}
            title={t('conferences:title')}
            onAdd={handleCreate}
            addButtonText={t('common:add')}
            translations={{
              items: t('common:items'),
              columns: t('common:columns'),
              noItemsFound: t('conferences:noConferencesFound'),
              showing: t('common:showing'),
              to: t('common:to'),
              of: t('common:of'),
              results: t('common:results'),
              previous: t('common:previous'),
              next: t('common:next'),
              page: t('common:page'),
              deleteItem: t('common:delete'),
              areYouSureDeleteItem: t('common:areYouSure'),
              delete: t('common:delete'),
              cancel: t('common:cancel'),
              selectAll: t('common:selectAll'),
              clearAll: t('common:clearAll'),
              allSelected: t('common:allSelected'),
              selected: t('common:selected'),
              searchOptions: t('common:searchOptions'),
              noOptionsFound: t('common:noOptionsFound')
            }}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, conference: null })}
            onConfirm={handleDeleteConfirm}
            title={t('conferences:messages.deleteConfirm')}
            message={deleteModal.conference
              ? `${t('conferences:messages.deleteConfirmMessage')}\n\nExtension: ${deleteModal.conference.extension}${deleteModal.conference.name ? `\nName: ${deleteModal.conference.name}` : ''}`
              : ''
            }
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
          />

          {xmlModal.isOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('conferences:xmlViewer.title')} - {xmlModal.conference?.extension}
                  </h3>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, conference: null, xml: '' })}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                    {xmlModal.xml}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(xmlModal.xml);
                      showSuccess(t('conferences:messages.xmlCopied'));
                    }}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    {t('common:copy')}
                  </button>
                  <button
                    onClick={() => setXmlModal({ isOpen: false, conference: null, xml: '' })}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    {t('common:close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      }
      formView={null}
    />
  );
}
